import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import { checkRouteAccess } from '@/config/access-control.config'
import { useAuthStore } from '@/stores/auth'
import { useUserStore } from '@/stores/user'

// 权限配置存储
const accessConfig = ref<{
  access_pages?: string[]
  access_channel_groups?: string[]
}>({})

// 是否已加载权限配置
const isAccessConfigLoaded = ref(false)

export function useAccessControl() {
  const userStore = useUserStore()
  const authStore = useAuthStore()
  const router = useRouter()

  // Check if Access Control feature is available for paid tiers
  const isAccessControlAvailable = computed(() => {
    const plan = String(
      userStore.user?.sub_items_price?.plan?.item_plan
      || (userStore as typeof userStore & { subscriptionPlan?: string }).subscriptionPlan
      || 'free',
    ).toLowerCase()

    return plan === 'pro' || plan === 'enterprise'
  })

  // 设置权限配置（在初始化时调用）
  const setAccessConfig = (config: { access_pages?: string[], access_channel_groups?: string[] }) => {
    accessConfig.value = config
    isAccessConfigLoaded.value = true
  }

  // 清除权限配置（在登出时调用）
  const clearAccessConfig = () => {
    accessConfig.value = {}
    isAccessConfigLoaded.value = false
  }

  // 获取当前权限配置
  const getAccessConfig = () => {
    return accessConfig.value
  }

  /**
   * 检查用户是否有访问指定路由路径的权限
   * @param routePath 路由路径，如 /dashboard, /settings/account
   * @returns 是否有权限访问
   */
  const checkPageAccess = (routePath: string | undefined | null): boolean => {
    if (!routePath)
      return true

    // If user is admin or owner, they have full access
    if (userStore.user.administer_flag || userStore.user.creator_flag) {
      return true
    }

    // 如果权限配置未加载，默认允许访问
    if (!isAccessConfigLoaded.value) {
      return true
    }

    // 使用配置文件中的权限检查逻辑
    return checkRouteAccess(routePath, accessConfig.value.access_pages)
  }

  // Check if the current user has access to a specific channel group
  // 注：channel groups 暂时不实现，预留接口
  const checkChannelAccess = (channelCode: string): boolean => {
    // Admin has full access
    if (userStore.user.administer_flag || userStore.user.creator_flag) {
      return true
    }

    // 如果权限配置未加载，默认允许访问
    if (!isAccessConfigLoaded.value) {
      return true
    }

    const channels = accessConfig.value.access_channel_groups
    if (!channels || channels.length === 0) {
      return true
    }

    return channels.includes(channelCode)
  }

  return {
    isAccessControlAvailable,
    isAccessConfigLoaded,
    accessConfig,
    setAccessConfig,
    clearAccessConfig,
    getAccessConfig,
    checkPageAccess,
    checkChannelAccess,
  }
}
